import React, { useEffect, useState } from "react";
import { Modal, Table, Switch, Tag, message, Button, Tooltip, Input } from "antd";
import { ColumnsType, ColumnType } from "antd/es/table";
import { PostJutgePracticaIdDocumentId } from "../../../../dao/jutge/PostJutgePracticaIdDocumentId";
import { GetJutgePracticaIdDocuments } from "../../../../dao/jutge/GetJutgePracticaIdDocuments";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import EditDocumentModal from "./EditDocumentModal";
import JutgeColors from "../../../../styles/JutgeColors";
import { BiPlus } from "react-icons/bi";

interface DocumentModalProps {
    id_practica: number;
    practicaNom?: string;
    visible: boolean;
    onClose: (updated: boolean) => void;
}

interface DocumentType {
    id: number;
    nom: string;
    tipo: string;
    visible: number;
    descripcio?: string;
    enunciat?: string;
}

const DocumentModal: React.FC<DocumentModalProps> = ({
    id_practica,
    practicaNom,
    visible,
    onClose,
}) => {
    const [loading, setLoading] = useState(false);
    const [documents, setDocuments] = useState<DocumentType[]>([]);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState<DocumentType | null>(null);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        if (visible && id_practica) {
            loadDocuments();
        }
    }, [visible]);

    async function loadDocuments() {
        try {
            setLoading(true);
            const data = await GetJutgePracticaIdDocuments(id_practica + "");
            // Ordenar alfabéticamente por nombre
            const sorted = (data || []).sort((a: DocumentType, b: DocumentType) => a.nom.localeCompare(b.nom));
            setDocuments(sorted);
        } catch (err) {
            console.error(err);
            message.error("Error carregant documents");
        } finally {
            setLoading(false);
        }
    }

    const handleToggle = async (checked: boolean, record: DocumentType) => {
        try {
            setDocuments((prev) =>
                prev.map((doc) =>
                    doc.id === record.id ? { ...doc, visible: checked ? 1 : 0 } : doc
                )
            );

            await PostJutgePracticaIdDocumentId(id_practica + "", record.id);

            message.success(
                checked
                    ? `Document "${record.nom}" activat`
                    : `Document "${record.nom}" desactivat`
            );
        } catch (err) {
            console.error(err);
            message.error("Error actualitzant document");
        }
    };

    const handleEditClick = (record: DocumentType) => {
        setSelectedDoc(record);
        setEditModalVisible(true);
    };

    const handleNewDocument = () => {
        setSelectedDoc({
            id: 0,
            nom: "",
            tipo: "enunciat",
            visible: 1,
            enunciat: "",
            descripcio: "",
        });
        setEditModalVisible(true);
    };

    // Filtrado y ordenamiento local según Actiu y Descripcio
    const filteredData = documents.filter((doc) =>
        doc.descripcio
            ? doc.descripcio.toLowerCase().includes(searchText.toLowerCase())
            : false
    );

    const columns: ColumnsType<DocumentType> = [
        {
            title: "Nom",
            dataIndex: "nom",
            key: "nom",
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: "Tipus",
            dataIndex: "tipo",
            key: "tipo",
            render: (tipo) => <Tag color="blue">{tipo}</Tag>,
        },
        {
            title: "Descripcio",
            dataIndex: "descripcio",
            key: "descripcio",
            render: (text) => <pre>{text}</pre>,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Cerca descripcio"
                        value={selectedKeys[0]}
                        onChange={e =>
                            setSelectedKeys(e.target.value ? [e.target.value] : [])
                        }
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: "block" }}
                    />
                    <Button
                        type="primary"
                        onClick={() => confirm()}
                        size="small"
                        style={{ width: 90, marginRight: 8 }}
                    >
                        Cercar
                    </Button>
                    <Button onClick={() => { clearFilters?.(); setSearchText(""); }} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </div>
            ),
            onFilter: (value, record: DocumentType) =>
                record.descripcio
                    ? record.descripcio.toLowerCase().includes((value as string).toLowerCase())
                    : false,
        },
        {
            title: "Actiu",
            dataIndex: "visible",
            key: "visible",
            align: "center",
            render: (visible, record) => (
                <Switch
                    checked={visible === 1}
                    onChange={(checked) => handleToggle(checked, record)}
                />
            ),
        },
        {
            title: "Accions",
            key: "actions",
            align: "center",
            width: 100,
            fixed: "right",
            render: (_, record) => (
                <Tooltip title="Editar document">
                    <Button
                        style={{ backgroundColor: JutgeColors.colors.color5 }}
                        shape="circle"
                        icon={<EditOutlined />}
                        onClick={() => handleEditClick(record)}
                    />
                </Tooltip>
            ),
        },
    ];


    return (
        <>
            <Modal
                open={visible}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: '1.2rem', fontWeight: 600 }}>📄 Documents & Tests de la Pràctica</span>
                            <Tag color="blue">{practicaNom || `ID ${id_practica}`}</Tag>
                        </div>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleNewDocument}
                            style={{ borderRadius: 6 }}
                        >
                            Nou Document
                        </Button>
                    </div>
                }
                onCancel={() => onClose(false)}
                width="75%"
                footer={null}
                centered
            >
                <div style={{ marginBottom: 12 }}>
                    <Input.Search
                        placeholder="Filtrar per descripció o text..."
                        allowClear
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ maxWidth: 300 }}
                    />
                </div>
                <Table
                    columns={columns}
                    dataSource={filteredData.length > 0 || !searchText ? (searchText ? filteredData : documents) : []}
                    loading={loading}
                    rowKey="id"
                    pagination={{ pageSize: 8 }}
                    bordered
                    size="small"
                />
            </Modal>

            {selectedDoc && (
                <EditDocumentModal
                    visible={editModalVisible}
                    document={selectedDoc}
                    idPractica={id_practica + ""}
                    practicaNom={practicaNom}
                    onClose={(updated: any) => {
                        setEditModalVisible(false);
                        setSelectedDoc(null);
                        if (updated) loadDocuments();
                    }}
                />
            )}
        </>
    );
};

export default DocumentModal;
